import { useEffect } from 'react';

import type {
    StickyCtaUpdateOffsetsRef,
} from './useStickyCtaOffsets';

export const STICKY_CTA_DISMISSED_KEY =
    'stickyCtaDismissed';

export const STICKY_BAR_STICK_CLASS =
    'stickyBarStick';

export const STICKY_BAR_BEHAVIOUR_CLASS =
    'stickyBarBehavior';

export type ScrollDirection = 'up' | 'down';

export function shouldStick(
    direction: ScrollDirection,
    position: number | undefined,
    dismissed: boolean,
    behavior: string,
): boolean | undefined {
    if (dismissed) {
        return false;
    }

    if (behavior === 'Global Nav' && direction === 'up') {
        return false;
    }

    if (position !== undefined) {
        if (position <= -1) {
            return true;
        }

        if (behavior !== 'Global Nav') {
            return false;
        }
    }

    return undefined;
}

function isStickyCtaDismissed(): boolean {
    return Boolean(
        sessionStorage.getItem(
            STICKY_CTA_DISMISSED_KEY,
        ),
    );
}

interface UseStickyCtaScrollBehaviorProps {
    scrollUpBehavior: string;
    isTopLevelSticky: boolean;
    triggerRef: React.RefObject<HTMLDivElement | null>;
    updateOffsetsRef: StickyCtaUpdateOffsetsRef;
}

export function useStickyCtaScrollBehavior({
    scrollUpBehavior,
    isTopLevelSticky,
    triggerRef,
    updateOffsetsRef,
}: UseStickyCtaScrollBehaviorProps): void {
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const navEntries =
            performance.getEntriesByType?.('navigation');

        const navEntry =
            navEntries?.[0] as
            | PerformanceNavigationTiming
            | undefined;

        if (navEntry?.type === 'back_forward') {
            sessionStorage.removeItem(
                STICKY_CTA_DISMISSED_KEY,
            );
        }
        if (
            navEntry?.type === 'reload' ||
            navEntry?.type === 'back_forward'
        ) {
            document.body.classList.remove(
                STICKY_BAR_STICK_CLASS,
            );
        }
    }, []);

    useEffect(() => {
        let lastScroll = 0;

        const isStickyBarBehavior =
            scrollUpBehavior !== 'Global Nav';

        document.body.classList.toggle(
            STICKY_BAR_BEHAVIOUR_CLASS,
            isStickyBarBehavior,
        );

        if (
            isTopLevelSticky &&
            isStickyBarBehavior &&
            !isStickyCtaDismissed()
        ) {
            document.body.classList.add(
                STICKY_BAR_STICK_CLASS,
            );

            updateOffsetsRef.current();
        }

        function scrollHandler() {
            const nav =
                document.querySelector<HTMLElement>(
                    '[data-componenttype="Global Nav"]',
                );

            const isSearchOpen =
                nav?.dataset.searchOpen === 'true';

            if (isSearchOpen) {
                const top =
                    triggerRef.current
                        ?.getBoundingClientRect()
                        ?.top;

                const shouldShowStickyDuringSearch =
                    isTopLevelSticky ||
                    (top !== undefined && top <= -1);

                document.body.classList.toggle(
                    STICKY_BAR_STICK_CLASS,
                    shouldShowStickyDuringSearch,
                );

                updateOffsetsRef.current();

                return;
            }

            const dismissed =
                isStickyCtaDismissed();

            if (dismissed) {
                document.body.classList.remove(
                    STICKY_BAR_STICK_CLASS,
                );

                return;
            }

            const top =
                triggerRef.current
                    ?.getBoundingClientRect()
                    ?.top;
            const direction: ScrollDirection =
                lastScroll < window.scrollY
                    ? 'down'
                    : 'up';

            lastScroll = window.scrollY;

            const scrollPosition =
                window.innerHeight +
                window.scrollY;

            const bottomPosition =
                document.body.offsetHeight - 100;

            if (
                scrollPosition >=
                bottomPosition
            ) {
                document.body.classList.remove(
                    STICKY_BAR_STICK_CLASS,
                );

                return;
            }

            if (window.scrollY < 10) {
                if (
                    isTopLevelSticky &&
                    isStickyBarBehavior &&
                    !isStickyCtaDismissed()
                ) {
                    document.body.classList.add(
                        STICKY_BAR_STICK_CLASS,
                    );

                    updateOffsetsRef.current();
                } else {
                    document.body.classList.remove(
                        STICKY_BAR_STICK_CLASS,
                    );
                }

                return;
            }

            const stickDecision = shouldStick(
                direction,
                top,
                dismissed,
                scrollUpBehavior,
            );

            if (stickDecision === true) {
                document.body.classList.add(
                    STICKY_BAR_STICK_CLASS,
                );
            } else if (stickDecision === false) {
                document.body.classList.remove(
                    STICKY_BAR_STICK_CLASS,
                );
            }

            if (
                scrollUpBehavior === 'Global Nav' &&
                direction === 'up'
            ) {
                updateOffsetsRef.current();
            }
        }

        document.addEventListener(
            'scroll',
            scrollHandler,
        );

        return () => {
            document.removeEventListener(
                'scroll',
                scrollHandler,
            );

            document.body.classList.remove(
                STICKY_BAR_BEHAVIOUR_CLASS,
            );
        };
    }, [
        scrollUpBehavior,
        isTopLevelSticky,
        triggerRef,
        updateOffsetsRef,
    ]);
}